/**
 * TappaReaderView — Stage view showing a single tappa (author × concept).
 *
 * Displays: spiegazione, contesto storico, cosa cambia, citazioni bilingui.
 * Navigates back to the author's detail view or to related authors via
 * clickable relation chips.
 */

import { BaseComponent } from '@/core/BaseComponent';
import type { ComponentContext } from '@/core/BaseComponent';
import { getDataService } from '@/services/data-registry';
import type { TappaLocalized, ConceptLocalized, ArgomentoLocalized } from '@/data/schema';
import { SlidePresenter, textToSlides } from '@/components/SlidePresenter';
import type { Slide } from '@/components/SlidePresenter';

export class TappaReaderView extends BaseComponent {
  private readonly tappaId: string;
  private presenter: SlidePresenter | null = null;
  private tappa: TappaLocalized | null = null;
  private authorName = '';
  /** click delegato su this.element: attaccare UNA volta (vedi AuthorDetailView). */
  private wired = false;

  constructor(element: HTMLElement, context: ComponentContext, tappaId: string) {
    super(element, context);
    this.tappaId = tappaId;
  }

  protected async onInit(): Promise<void> {
    this.element.classList.add('tappa-reader-view');
    const data = getDataService();
    this.tappa = await data.getTappa(this.tappaId, this.context.locale);

    if (!this.tappa) {
      this.renderNotFound();
      return;
    }

    // Load related entities + sibling tappe for navigation
    const [author, siblingTappe, concepts, argomento] = await Promise.all([
      data.getAuthor(this.tappa.autore_id, this.context.locale),
      data.getTappeByAuthor(this.tappa.autore_id, this.context.locale),
      data.listConcepts(this.context.locale),
      this.tappa.argomento_id
        ? data.getArgomento(this.tappa.argomento_id, this.context.locale)
        : Promise.resolve(null),
    ]);
    const conceptMap = new Map(concepts.map((c) => [c.id, c]));
    this.authorName = author?.nome ?? this.tappa.autore_id;

    if (this.isLimMode()) {
      this.renderLim(conceptMap);
    } else {
      this.render(this.tappa, author, siblingTappe, conceptMap, argomento);
      this.wireEvents();
    }

    // React to display mode changes
    this.addCleanup(
      this.context.bus.on('display:change', async () => {
        if (!this.tappa) return;
        this.disposePresenter();
        this.element.innerHTML = '';
        if (this.isLimMode()) {
          this.renderLim(conceptMap);
        } else {
          this.render(this.tappa, author, siblingTappe, conceptMap, argomento);
          this.wireEvents();
        }
      }),
    );
  }

  private isLimMode(): boolean {
    return document.documentElement.getAttribute('data-display') === 'lim';
  }

  private disposePresenter(): void {
    if (this.presenter) {
      this.presenter.dispose();
      this.presenter = null;
    }
  }

  protected onDispose(): void {
    this.disposePresenter();
  }

  private renderLim(conceptMap: Map<string, ConceptLocalized>): void {
    const tappa = this.tappa!;
    const conceptName = conceptMap.get(tappa.concetto_id)?.nome ?? tappa.concetto_id;
    const slides = this.buildSlides(tappa, conceptName);
    this.presenter = new SlidePresenter(this.element, slides, {
      onExit: () => {
        this.context.bus.emit('navigate:back', {});
      },
      backLabel: 'Indietro',
    });
    this.presenter.mount();
  }

  private buildSlides(tappa: TappaLocalized, conceptName: string): Slide[] {
    const slides: Slide[] = [];
    const c = tappa.content;

    // Title slide
    slides.push({
      sectionTitle: conceptName,
      html: `
        <h1 class="sp-title">${esc(this.authorName)}</h1>
        <p class="sp-concept">${esc(conceptName)}</p>
        ${tappa.anno_riferimento ? `<p class="sp-anno">${formatYear(tappa.anno_riferimento)}</p>` : ''}
      `,
    });

    // La tesi
    slides.push(...textToSlides(c.spiegazione, 'La tesi'));

    // Contesto storico
    slides.push(...textToSlides(c.contesto_storico, 'Contesto storico'));

    // Perché è importante
    slides.push(...textToSlides(c.cosa_cambia, 'Perché è importante'));

    // Citazioni
    for (const cit of c.citazioni) {
      const meta = tappa.citazioni.find((m) => m.id === cit.id);
      const rif = meta?.riferimento;
      const rifLabel = rif ? [rif.titolo, rif.edizione, rif.passaggio].filter(Boolean).join(', ') : '';
      slides.push({
        sectionTitle: 'Citazione',
        html: `
          <blockquote class="sp-quote-original">${esc(cit.testo_originale)}</blockquote>
          <blockquote class="sp-quote-translation">${esc(cit.testo_tradotto)}</blockquote>
          ${rifLabel ? `<p class="sp-quote-source">— ${esc(rifLabel)}</p>` : ''}
        `,
      });
    }

    return slides;
  }

  private renderNotFound(): void {
    this.element.innerHTML = `
      <div class="empty-state">
        <p>Tappa non trovata: <code>${esc(this.tappaId)}</code></p>
        <button type="button" class="btn" data-action="home">Torna alla lista</button>
      </div>
    `;
    this.addEventListener(this.element, 'click', () => {
      this.context.bus.emit('navigate:home', {});
    });
  }

  private render(
    tappa: TappaLocalized,
    author: { nome: string } | null,
    siblingTappe: TappaLocalized[],
    conceptMap: Map<string, ConceptLocalized>,
    argomento: ArgomentoLocalized | null,
  ): void {
    const authorName = author?.nome ?? tappa.autore_id;

    const citazioniHtml = tappa.content.citazioni.length > 0
      ? tappa.content.citazioni.map(c => {
        const meta = tappa.citazioni.find(m => m.id === c.id);
        const rif = meta?.riferimento;
        const rifLabel = rif
          ? [rif.titolo, rif.edizione, rif.passaggio].filter(Boolean).join(', ')
          : '';
        return `
            <figure class="tappa-citazione" data-fragment-id="tappa:${attr(tappa.id)}/citazione/${attr(c.id)}">
              <blockquote class="tappa-citazione-originale" lang="${attr(meta?.lingua_originale ?? 'la')}">${esc(c.testo_originale)}</blockquote>
              <blockquote class="tappa-citazione-tradotta">${esc(c.testo_tradotto)}</blockquote>
              ${rifLabel ? `<figcaption class="tappa-citazione-fonte">— ${esc(rifLabel)}</figcaption>` : ''}
            </figure>
          `;
      }).join('')
      : '';

    // Approfondisci: link al grande argomento canonico se collegato
    const approfondiHtml = argomento
      ? (() => {
          const enunciato = argomento.content.enunciato.length > 220
            ? argomento.content.enunciato.slice(0, 220).replace(/\s+\S*$/, '…')
            : argomento.content.enunciato;
          const anno = argomento.anno_formulazione
            ? ` <span class="argomento-card-anno">(${argomento.anno_formulazione})</span>`
            : '';
          return `
            <section class="tappa-section tappa-approfondisci">
              <h2>Approfondisci l'argomento</h2>
              <div class="argomento-list">
                <button type="button" class="argomento-card" data-argomento-link="${attr(argomento.id)}">
                  <span class="argomento-card-name">${esc(argomento.nome)}${anno}</span>
                  <span class="argomento-card-statement">${esc(enunciato)}</span>
                </button>
              </div>
            </section>
          `;
        })()
      : '';

    // Build sibling navigation chips
    const siblingNav = siblingTappe.length > 1
      ? `<nav class="tappa-siblings">
          ${siblingTappe.map((s) => {
            const cName = conceptMap.get(s.concetto_id)?.nome ?? s.concetto_id;
            const isCurrent = s.id === tappa.id;
            return `<button type="button" class="chip ${isCurrent ? 'chip-active' : 'chip-link'}" data-tappa-nav="${attr(s.id)}" ${isCurrent ? 'disabled' : ''}>${esc(cName)}</button>`;
          }).join('')}
        </nav>`
      : '';

    this.element.innerHTML = `
      <div class="tappa-topbar">
        <button type="button" class="btn btn-back" data-action="back">← Indietro</button>
      </div>

      <header class="tappa-header">
        <h1 class="tappa-title">${esc(authorName)}</h1>
        ${tappa.anno_riferimento ? `<p class="tappa-anno-inline">${formatYear(tappa.anno_riferimento)}</p>` : ''}
        ${siblingNav}
      </header>

      <div class="tappa-sections">
        <details class="detail-fold tappa-section" open>
          <summary class="detail-section-title" data-fragment-id="tappa:${attr(tappa.id)}/tesi-titolo">La tesi</summary>
          <div class="tappa-body" data-fragment-id="tappa:${attr(tappa.id)}/tesi">${paragraphs(tappa.content.spiegazione)}</div>
        </details>

        <details class="detail-fold tappa-section" open>
          <summary class="detail-section-title" data-fragment-id="tappa:${attr(tappa.id)}/contesto-titolo">Contesto storico</summary>
          <div class="tappa-body" data-fragment-id="tappa:${attr(tappa.id)}/contesto">${paragraphs(tappa.content.contesto_storico)}</div>
        </details>

        <details class="detail-fold tappa-section" open>
          <summary class="detail-section-title" data-fragment-id="tappa:${attr(tappa.id)}/cosa-cambia-titolo">Perché è importante</summary>
          <div class="tappa-body" data-fragment-id="tappa:${attr(tappa.id)}/cosa-cambia">${paragraphs(tappa.content.cosa_cambia)}</div>
        </details>

        ${citazioniHtml ? `
          <details class="detail-fold tappa-section tappa-citazioni-section" open>
            <summary class="detail-section-title" data-fragment-id="tappa:${attr(tappa.id)}/citazioni-titolo">Citazioni</summary>
            ${citazioniHtml}
          </details>
        ` : ''}
      </div>

      ${approfondiHtml}
    `;
  }

  private wireEvents(): void {
    if (this.wired) return;
    this.wired = true;
    this.addEventListener(this.element, 'click', (ev) => {
      const t = ev.target as HTMLElement;
      if (t.closest<HTMLElement>('[data-action="back"]')) {
        this.context.bus.emit('navigate:back', {});
        return;
      }
      const tappaNav = t.closest<HTMLElement>('[data-tappa-nav]');
      if (tappaNav?.dataset.tappaNav) {
        this.context.bus.emit('navigate:tappa', { id: tappaNav.dataset.tappaNav });
        return;
      }
      const argomentoLink = t.closest<HTMLElement>('[data-argomento-link]');
      if (argomentoLink?.dataset.argomentoLink) {
        this.context.bus.emit('navigate:argomento', { id: argomentoLink.dataset.argomentoLink });
        return;
      }
      if (t.dataset.action === 'home') {
        this.context.bus.emit('navigate:home', {});
      }
    });
  }
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function attr(s: string): string {
  return esc(s).replace(/"/g, '&quot;');
}

function formatYear(y: number): string {
  if (y < 0) return `${Math.abs(y)} a.C.`;
  return `${y} d.C.`;
}

function paragraphs(text: string): string {
  return text
    .split(/\n\n+/)
    .map(p => `<p>${esc(p.trim())}</p>`)
    .join('');
}
